import { useState } from "react";
import { Box, Button, Link, Stack, TextField, Typography } from "@mui/material";
import { useEstateAppApiContext } from "src/contexts/estateAppApi-context";
import useHousehold from "src/contexts/household-context";
import { StripePaymentIntent } from "src/types/stripe";

interface PromoCodeFieldProps {
  ud_id: string | null;
  paymentIntent: StripePaymentIntent;
  product: string;
  paymentScope?: "INDIVIDUAL" | "HOUSEHOLD";
  onPaymentIntentChange: (paymentIntent: StripePaymentIntent) => void;
  // False when the surrounding checkout shows the discount in its own order summary
  showTotals?: boolean;
}

const dollars = (cents: number) =>
  `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// The server works out the discount and updates the payment intent; this only sends the code.
export function PromoCodeField(props: PromoCodeFieldProps) {
  const { ud_id, paymentIntent, product, paymentScope, onPaymentIntentChange, showTotals = true } = props;
  const { estateAppApiInstance } = useEstateAppApiContext();
  const { selectedHousehold } = useHousehold();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const appliedCode: string | undefined = paymentIntent.metadata?.promo_code || undefined;
  const discount = Number(paymentIntent.metadata?.discount_amount || 0);

  const submit = async (promoCode: string) => {
    if (!ud_id) return;
    setSaving(true);
    setError(null);
    try {
      const response = await estateAppApiInstance.put(`/v3/${ud_id}/payments/${paymentIntent.id}/`, {
        product,
        payment_scope: paymentScope,
        promo_code: promoCode,
      });
      onPaymentIntentChange(response.data.data as StripePaymentIntent);
      setCode("");
      setOpen(false);
    } catch (err: any) {
      // Only a 400 carries a message meant for the client; anything else is a server fault
      const details = err?.response?.status === 400 ? err?.response?.data?.details : null;
      setError(details || "That promo code couldn't be applied. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Snug turns promo codes on per firm
  if (!selectedHousehold?.professional_group?.allow_promo_codes) {
    return null;
  }

  if (appliedCode) {
    return (
      <Stack spacing={0.5} sx={{ mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body2">
            Promo code <strong>{appliedCode}</strong>
            <Link
              component="button"
              type="button"
              variant="body2"
              sx={{ ml: 1 }}
              disabled={saving}
              onClick={() => submit("")}
            >
              Remove
            </Link>
          </Typography>
          {showTotals && <Typography variant="body2" color="success.main">−{dollars(discount)}</Typography>}
        </Stack>
        {showTotals && (
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" fontWeight={600}>Total due</Typography>
            <Typography variant="body2" fontWeight={600}>{dollars(paymentIntent.amount)}</Typography>
          </Stack>
        )}
        {error && <Typography variant="caption" color="text.secondary">{error}</Typography>}
      </Stack>
    );
  }

  if (!open) {
    return (
      <Box sx={{ mb: 2 }}>
        <Link component="button" type="button" variant="body2" onClick={() => setOpen(true)}>
          Have a promo code?
        </Link>
      </Box>
    );
  }

  return (
    <Stack spacing={0.5} sx={{ mb: 2 }}>
      <Stack direction="row" spacing={1}>
        <TextField
          size="small"
          label="Promo code"
          value={code}
          autoFocus
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && code.trim()) {
              e.preventDefault();
              submit(code);
            }
          }}
          sx={{ flexGrow: 1 }}
        />
        <Button variant="outlined" disabled={!code.trim() || saving} onClick={() => submit(code)} sx={{ borderRadius: 1 }}>
          Apply
        </Button>
      </Stack>
      {error && <Typography variant="caption" color="text.secondary">{error}</Typography>}
    </Stack>
  );
}
